export const RawPaymentStatuses = {
  COMPLETED: 'completed',
  PENDING: 'pending',
  FAILED: 'failed',
  REFUNDED: 'refunded',
  DECLINED: 'declined',
  AUTHORIZED: 'authorized',
  SETTLED: 'settled',
  PROCESSING: 'processing',
  SUCCEEDED: 'succeeded',
} as const;

export type RawPaymentStatus = (typeof RawPaymentStatuses)[keyof typeof RawPaymentStatuses];

export const UiPaymentStatuses = {
  SUCCEEDED: 'succeeded',
  PENDING: 'pending',
  FAILED: 'failed',
  REFUNDED: 'refunded',
  PARTIALLY_REFUNDED: 'partially_refunded',
} as const;

export type UiPaymentStatus = (typeof UiPaymentStatuses)[keyof typeof UiPaymentStatuses];

export const CollectionTypes = {
  DEBT_COLLECTION: 'debt_collection',
  SUBSCRIPTION_RECOVERY: 'recurring_recovery',
} as const;

export type CollectionType = (typeof CollectionTypes)[keyof typeof CollectionTypes];

export const PaymentMethodTypes = {
  PAYMENT_CARD: 'PAYMENT_CARD',
  DEBIT: 'DEBIT',
  GOOGLE_PAY: 'GOOGLE_PAY',
  APPLE_PAY: 'APPLE_PAY',
} as const;

export type PaymentMethodType = (typeof PaymentMethodTypes)[keyof typeof PaymentMethodTypes];

export const PaymentCardNetworks = {
  VISA: 'VISA',
  MASTERCARD: 'MASTERCARD',
} as const;

export type PaymentCardNetwork = (typeof PaymentCardNetworks)[keyof typeof PaymentCardNetworks];

export const PaymentProcessors = {
  PAYPAL: 'paypal',
} as const;

export type PaymentProcessor = (typeof PaymentProcessors)[keyof typeof PaymentProcessors];

/** lower-cased provider values surfaced on `payment.provider`. */
export const PaymentProviders = {
  PRIMER: 'primer',
  YUNO: 'yuno',
} as const;

export type PaymentProvider = (typeof PaymentProviders)[keyof typeof PaymentProviders];

/** upper-cased provider status values surfaced on `payment.providerStatus`. */
export const PaymentProviderStatuses = {
  AUTHORIZED: 'AUTHORIZED',
  SETTLED: 'SETTLED',
  COMPLETED: 'COMPLETED',
  PENDING: 'PENDING',
  CANCELLED: 'CANCELLED',
  DECLINED: 'DECLINED',
  FAILED: 'FAILED',
} as const;

export type PaymentProviderStatus =
  (typeof PaymentProviderStatuses)[keyof typeof PaymentProviderStatuses];

/**
 * Where a payment stands in the delayed-capture pipeline. Derived by the API from the capture
 * timestamps and the `delayed_capture.*` windows — never stored on the payment row.
 */
export const CaptureStates = {
  CAPTURED: 'captured',
  AWAITING: 'awaiting',
  REQUESTED: 'requested',
  EXPIRED: 'expired',
  MIGRATED: 'migrated',
  NOT_APPLICABLE: 'not_applicable',
} as const;

export type CaptureState = (typeof CaptureStates)[keyof typeof CaptureStates];

export const BrandCodes = {
  KBM: 'kbm',
  FBM: 'fbm',
  YOUTH_HG: 'youth-hg',
  KG: 'kg',
  SBM: 'sbm',
  CF: 'cf',
  BM: 'bm',
  CKD: 'ckd',
  NM: 'nm',
  MF: 'mf',
  SD: 'sd',
} as const;

export type BrandCode = (typeof BrandCodes)[keyof typeof BrandCodes];

export const PaymentSearchByFields = {
  PAYMENT_ID: 'payment_id',
  PROVIDER_ID: 'provider_id',
  PAYMENT_SESSION_ID: 'payment_session_id',
  SUBSCRIPTION_ID: 'subscription_id',
  CUSTOMER_EMAIL: 'customer_email',
  CUSTOMER_ID: 'customer_id',
  CUSTOMER_EXTERNAL_ID: 'customer_external_id',
} as const;

export type PaymentSearchByField =
  (typeof PaymentSearchByFields)[keyof typeof PaymentSearchByFields];

export type PaymentTypeKey = 'debtCollection' | 'scheduledPayment' | 'subscriptionRecovery';

export type PaymentBrand = {
  id: string;
  brandCode: string;
  brandName: string;
};

export type PaymentCurrency = {
  id: string;
  code: string;
  symbol: string;
  decimalDigits: number;
};

export type PaymentSessionCustomer = {
  email: string;
  firstName: string | null;
  lastName: string | null;
  externalId: string | null;
  country: string | null;
  locale: string;
};

export type PaymentSessionOffer = {
  id: string;
  type: string | null;
};

export type PaymentSessionPriceProductObject = {
  id: string;
  features: string[];
};

export type SubscriptionInfo = {
  id: string;
  paidTill: string;
  canceledAt: string | null;
  createdAt: string;
};

export type DebtCollectionInfo = {
  id: string;
  executeAt: string;
  status: string;
  retryTime: number;
  createdAt: string;
};

export type ScheduledPaymentInfo = {
  id: string;
  executeAt: string;
  status: string;
  chargerType: string;
  chargerVersion: number;
  createdAt: string;
};

export type SubscriptionRecoveryInfo = {
  id: string;
  attempt: number;
  executeAt: string;
  status: string;
  createdAt: string;
};

export type PaymentSession = {
  id: string;
  numericId: string;
  provider: string;
  amount: number;
  paymentDescriptor: string;
  source: string;
  platform: string;
  createdAt: string;
  updatedAt: string;
  ip: string;
  customer: PaymentSessionCustomer;
  offer?: PaymentSessionOffer | null;
  priceProductObject?: PaymentSessionPriceProductObject | null;
  subscription: SubscriptionInfo | null;
  debtCollection: DebtCollectionInfo | null;
  scheduledPayment: ScheduledPaymentInfo | null;
  subscriptionRecovery: SubscriptionRecoveryInfo | null;
};

/** BIN / card metadata from the processor (payment card flows). */
export type PaymentCardBinData = {
  network: string;
  issuerName: string;
  productCode: string;
  productName: string;
  productUsageType: string;
  accountNumberType: string;
  issuerCountryCode: string;
  accountFundingType: string;
  issuerCurrencyCode: string;
  regionalRestriction: string;
  prepaidReloadableIndicator: string;
};

export type PaymentCardPaymentMethodData = {
  binData: PaymentCardBinData;
  network: string;
  last4Digits: string;
  first6Digits: string;
  cardholderName: string | null;
  expirationYear: string;
  expirationMonth: string;
  isNetworkTokenized: boolean;
};

export type CustomerPaymentMethodThreeDSecureAuthentication = {
  responseCode: string;
};

export type CustomerPaymentMethodDetails = {
  isVaulted: boolean;
  descriptor: string;
  analyticsId: string;
  paymentType: string;
  authorizationType: string;
  paymentMethodData: PaymentCardPaymentMethodData;
  paymentMethodType: string;
  paymentMethodToken: string;
  threeDSecureAuthentication: CustomerPaymentMethodThreeDSecureAuthentication;
};

/** Charger flow snapshot on `paymentProviderError` (failed payment diagnostics). */
export type PaymentProviderErrorChargerFlow = {
  id: string;
  name?: string | null;
  createdAt?: string;
  updatedAt?: string;
  type?: string;
};

export type PaymentProviderError = {
  chargerFlowId?: string | null;
  chargerFlow?: PaymentProviderErrorChargerFlow | null;
};

export type CustomerPaymentMethod = {
  id: string;
  status: string;
  customerId: string;
  lastErrorCodeId: string | null;
  provider: string;
  providerToken: string;
  externalOrderId: string;
  externalOrderToken: string;
  type: string;
  network: string;
  firstName: string | null;
  lastName: string | null;
  billingAddressFirstName: string | null;
  billingAddressLastName: string | null;
  legalCompany: string;
  processorMerchantId: string;
  firstDigits: string;
  lastDigits: string;
  issuerCountry: string;
  issuerName: string;
  expiresAt: string;
  details: CustomerPaymentMethodDetails;
  createdAt: string;
  updatedAt: string;
  preventRecurringPaymentsTill: string;
  recoveryPercentage: number | null;
  successRetryInterval: number | null;
  retryTimes: number;
};

export type Payment = {
  id: string;
  amount: number;
  amountInEuro: number;
  amountRefunded: number;
  provider: string;
  providerId: string;
  status: RawPaymentStatus;
  providerStatus: string;
  providerDeclineType: string | null;
  providerDeclineCode: string | null;
  providerDeclineMessage: string | null;
  processor: string;
  legalCompany: string;
  processorMerchantId: string;
  completedAt: string;
  createdAt: string;
  updatedAt: string;
  vatCountry: string;
  capturedAt: string | null;
  lastCaptureAttemptAt: string | null;
  captureState?: CaptureState;
  captureWindowClosesAt?: string | null;
  authorizationCode: string | null;
  brand: PaymentBrand;
  currency: PaymentCurrency;
  customerPaymentMethod?: CustomerPaymentMethod | null;
  paymentProviderError?: PaymentProviderError | null;
  paymentSession: PaymentSession;
  collectionType: CollectionType | null;
};

export type PaymentSortField = 'date' | 'amount';
export type PaymentSortDir = 'asc' | 'desc';
export type PaymentSortState = {
  field: PaymentSortField;
  dir: PaymentSortDir;
};

export type PaymentFilterOption<T extends string = string> = {
  value: T | 'all';
  label: string;
};
